import { Box3, Object3D, Scene, Sphere, Vector3 } from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import {
  CameraInterface,
  Engine,
  Entity
} from "../../engine/Engine";
import { getCameraPresetFromBox } from "../../engine/camera/CameraPresets";
import {
  computeFitDistance,
  fitObjectToCamerInterface,
} from "../../engine/camera/CameraUtils";
import { lerp } from "../../util/MathUtil";

export interface SmoothOrbitOptions {
  alignLerp?: number;
  dampingFactor?: number;
  enableDamping?: boolean;
  /** When true, fit-camera clamps orbit maxDistance to fitDistance * 1.25 (storefront). */
  constrainFitMaxDistance?: boolean;
}

export class SmoothOrbitController extends Entity {
  private _orbitControls: OrbitControls;
  private _orbitTarget = new Vector3();
  private _orbitPosition = new Vector3();
  private _orbitAlignPosition = false;
  private _orbitAlignTarget = false;
  private _alignLerp: number;
  private _dampingFactor: number;
  private _enableDamping: boolean;
  private _constrainFitMaxDistance: boolean;

  private __orbitChangeFunctionInstance;
  private __orbitStartFunctionInstance;
  private _orbitUp = new Vector3(0, 1, 0);
  private _fitCameraInstance: any;

  constructor(orbitControl: OrbitControls, options?: SmoothOrbitOptions) {
    super();
    this._orbitControls = orbitControl;
    this._alignLerp = options?.alignLerp ?? 0.15;
    this._dampingFactor = options?.dampingFactor ?? 0.5;
    this._enableDamping = options?.enableDamping ?? true;
    this._constrainFitMaxDistance = options?.constrainFitMaxDistance ?? true;
    this._enabled = false;
    this.__orbitChangeFunctionInstance = this.onOrbitChange.bind(this);
    this.__orbitStartFunctionInstance = this.onOrbitStart.bind(this);
    this._fitCameraInstance = this.fitCamera.bind(this);
    this.applyDampingSettings();
  }

  setAlignLerp(value: number) {
    this._alignLerp = value;
  }

  setDampingFactor(value: number) {
    this._dampingFactor = value;
    this.applyDampingSettings();
  }

  private applyDampingSettings() {
    if (!this._orbitControls) return;
    console.log("applyDampingSettings", this._enableDamping, this._dampingFactor);
    this._orbitControls.enableDamping = this._enableDamping;
    this._orbitControls.dampingFactor = this._dampingFactor;
  }

  setOrbitTarget(value: Vector3, force = false) {
    this._orbitTarget = value;
    this._orbitAlignTarget = true;
    this.engine!.dispatchCustomEvent("camera-start");
    if (force) {
      this._orbitControls?.target.copy(value);
      this._orbitAlignTarget = false;
      this._orbitControls.update();
    }
  }

  setOrbitPosition(value: Vector3, force = false) {
    this._orbitPosition = value;
    this._orbitAlignPosition = true;
    this.engine!.dispatchCustomEvent("camera-start");
    if (force) {
      this._orbitControls?.object?.position.copy(value);
      this._orbitAlignPosition = false;
      this._orbitControls.update();
    }
  }

  cancelAnimation() {
    this._orbitAlignPosition = false;
    this._orbitAlignTarget = false;
    this._orbitControls.update();
  }

  onOrbitChange() {
    this.engine!.dispatchCustomEvent("request-update");
  }

  onOrbitStart() {
    this._orbitAlignPosition = false;
    this._orbitAlignTarget = false;
    this.engine!.dispatchCustomEvent("camera-start");
    this.engine!.dispatchCustomEvent("request-update");
  }

  onOrbitEnd() {
    this.engine!.dispatchCustomEvent("request-update");
    this.engine!.dispatchCustomEvent("camera-end");
  }

  animate(dt: number, scene?: Scene, camera?: CameraInterface): void {
    if (!this._orbitControls) {
      this._orbitControls = this.engine!.activeCamera!.controller;
      this.applyDampingSettings();
    }

    let wasAligningPosition = this._orbitAlignPosition;
    if (this._orbitAlignPosition) {
      const positionDist = this._orbitControls.object.position.distanceTo(
        this._orbitPosition,
      );

      this._orbitAlignPosition = positionDist > 0.001;
      const newPosition = lerp(
        this._orbitControls.object.position,
        this._orbitPosition,
        this._alignLerp,
      ) as Vector3;

      this._orbitControls.object.position.copy(newPosition);
      this._orbitControls.object.up.copy(this._orbitUp);
      this._orbitControls.object.lookAt(this._orbitControls.target);
    }

    let wasAligningTarget = this._orbitAlignTarget;
    if (this._orbitAlignTarget) {
      const targetDist = this._orbitControls.target.distanceTo(
        this._orbitTarget,
      );

      this._orbitAlignTarget = targetDist > 0.001;

      const newTarget = lerp(
        this._orbitControls.target,
        this._orbitTarget,
        this._alignLerp,
      ) as Vector3;
      this._orbitControls?.target.copy(newTarget!);
    }

    if (
      ((!this._orbitAlignTarget && wasAligningTarget) ||
        (!this._orbitAlignPosition && wasAligningPosition))
    ) {
      this.engine!.dispatchCustomEvent("camera-end");
    }

    //if (!this._orbitAlignPosition && !this._orbitAlignTarget) {
      this._orbitControls.update();
    //}

    requestAnimationFrame(() => {
      this.engine?.dispatchCustomEvent("camera-change", {
        detail: this.engine?.activeCamera?.instance,
      });
    });
  }

  onEnabled(): void {
    this.applyDampingSettings();
    this._orbitControls?.addEventListener(
      "start",
      this.__orbitStartFunctionInstance,
    );

    this._orbitControls?.addEventListener(
      "change",
      this.__orbitChangeFunctionInstance,
    );
  }

  onDisabled(): void {
    this._orbitControls?.removeEventListener(
      "change",
      this.__orbitChangeFunctionInstance,
    );
    this._orbitControls?.removeEventListener(
      "start",
      this.__orbitStartFunctionInstance,
    );
  }

  fitCamera(e: any) {
    const detail = e.detail;
    const engine = this.engine;
    const obj: Object3D | undefined =
      detail?.object ?? detail ?? engine?.activeScene?.children[0];
    // Ölçü fit'i preset taşımaz. Preset diğer seçeneklerin kamera açısını değiştirir.
    const preserveView = detail?.preserveView === true;
    const preset = preserveView
      ? undefined
      : (detail?.preset as string | undefined);

    const box = obj ? new Box3().setFromObject(obj, true) : undefined;

    let newSettings: { position: Vector3; target: Vector3 };

    if (preset && obj && box) {
      const cam = engine?.activeCamera?.instance;
      const fovDeg = (cam as any)?.fov ?? 25;
      const aspect = (cam as any)?.aspect ?? 1;
      if (preset === "top") {
        const front = getCameraPresetFromBox(
          box,
          "front",
          fovDeg,
          aspect,
        );
        if (!front) return;
        const center = front.target;
        const distance = Math.max(front.position.distanceTo(center), 1e-3);
        this._orbitUp.copy(center).sub(front.position).normalize();
        newSettings = {
          target: center.clone(),
          position: new Vector3(center.x, center.y + distance, center.z),
        };
      } else {
        const computed = getCameraPresetFromBox(
          box,
          preset,
          fovDeg,
          aspect,
        );
        if (!computed) return;
        newSettings = computed;
        this._orbitUp.set(0, 1, 0);
      }
    } else if (preserveView) {
      const cam = engine?.activeCamera?.instance as
        | { fov?: number; aspect?: number; position: Vector3; up: Vector3 }
        | undefined;
      if (!cam || !box || box.isEmpty()) return;
      const direction = cam.position.clone().sub(this._orbitControls.target);
      if (direction.lengthSq() < 1e-12) return;
      direction.normalize();
      const sphere = box.getBoundingSphere(new Sphere());
      const distance = computeFitDistance(
        Math.max(sphere.radius, 1e-3),
        cam.fov ?? 25,
        cam.aspect || 1,
        "legacy-aspect",
      );
      const target = sphere.center.clone();
      newSettings = {
        target,
        position: target.clone().addScaledVector(direction, distance),
      };
      this._orbitUp.copy(cam.up);
    } else {
      const camera = engine?.activeCamera;
      if (!camera || !obj) return;
      // Compute only — EngineCamera.fit() applies instantly and would skip lerp.
      const computed = fitObjectToCamerInterface(obj, camera);
      if (!computed) return;
      newSettings = computed;
      this._orbitUp.set(0, 1, 0);
    }

    if (box && this._constrainFitMaxDistance) {
      const cam = engine?.activeCamera?.instance;
      const fovDeg = (cam as any)?.fov ?? 25;
      const aspect = (cam as any)?.aspect ?? 1;
      const sphere = box.getBoundingSphere(new Sphere());
      const fitDistance = computeFitDistance(
        Math.max(sphere.radius, 1e-3),
        fovDeg,
        aspect,
        "legacy-aspect",
      );
      this._orbitControls.maxDistance = Math.max(fitDistance * 1.25, 0.1);
    }

    this._orbitControls.object.up.copy(this._orbitUp);
    this.setOrbitTarget(newSettings.target);
    this.setOrbitPosition(newSettings.position);
  }

  onAdd(engine: Engine): void {
    // The controls of the engine it joins: the element may have been made
    // while another engine was the page's active one (a view mounted again
    // after a lost GPU context). On a one-engine page they are the same.
    const controls = engine.activeCamera?.controller as OrbitControls | undefined;
    if (controls) this._orbitControls = controls;
    this.enabled = true;
    engine.addEventListener("fit-camera", this._fitCameraInstance);
  }

  onRemove(engine: Engine): void {
    this.enabled = false;
    engine.removeEventListener("fit-camera", this._fitCameraInstance);
  }
}
